'use client'

import CloseButton from '@components/CloseButton'
import SimpleSpinner from '@components/SimpleSpinner'
import { IdlAccounts } from '@coral-xyz/anchor'
import { useSolana } from '@dewicats/connect-button'
import { AuctionManager } from '@dewicats/idls/lib/types/auction_manager'
import { bulkSendRawTransactions } from '@helium/spl-utils'
import useAuctionManager from '@hooks/useAuctionManager'
import { PublicKey, Transaction } from '@solana/web3.js'
import React, { useCallback, useState } from 'react'
import { useAsync } from 'react-async-hook'
import { useTranslation } from 'react-i18next'

const CancelBidsBanner = () => {
  const { anchorProvider } = useSolana()
  const { getAllBidsForWallet, auctionProgram, auctionKey } =
    useAuctionManager()
  const [canceling, setCanceling] = useState(false)
  const [hide, setHide] = useState(false)
  const { t } = useTranslation()

  const { result: allUncanceledBids } = useAsync(async () => {
    if (!anchorProvider) return
    const allBids = await getAllBidsForWallet()
    const allUncanceledBids = allBids.filter((bid) => {
      // check bid is from at least 1 day ago
      const oneDayAgo = new Date()
      oneDayAgo.setDate(oneDayAgo.getDate() - 1)
      const bidDate = new Date(bid.createdAt.toNumber() * 1000)
      const isOldBid = bidDate < oneDayAgo

      return !bid.state.cancelled && isOldBid && !bid.state.executed
    })
    return allUncanceledBids
  }, [getAllBidsForWallet, anchorProvider])

  const onCancelAll = useCallback(async () => {
    if (!allUncanceledBids) return

    setCanceling(true)
    // batch bids into 2 each for each transaction
    const batchedBids = []
    let batch: (IdlAccounts<AuctionManager>['bidRecieptV0'] & {
      publicKey: PublicKey
    })[] = []
    allUncanceledBids.forEach((bid) => {
      batch.push(bid)
      if (batch.length === 2) {
        batchedBids.push(batch)
        batch = []
      }
    })
    if (batch.length > 0) {
      batchedBids.push(batch)
    }

    await Promise.all(
      batchedBids.map(async (batch) => {
        try {
          const ixs = await Promise.all(
            batch.map(async (bid) => {
              const listingAcc = await auctionProgram.account.listingV0.fetch(
                bid.listing,
              )
              return auctionProgram.methods
                .cancelBidV0({
                  bidReciept: bid.publicKey,
                })
                .accountsPartial({
                  listing: bid.listing,
                  bidReciept: bid.publicKey,
                  payer: anchorProvider.publicKey,
                  tokenMint: listingAcc.tokenMint,
                  auctionManager: auctionKey,
                })
                .instruction()
            }),
          )

          const txn = new Transaction().add(...ixs)

          txn.feePayer = anchorProvider.publicKey
          txn.recentBlockhash = (
            await anchorProvider.connection.getLatestBlockhash()
          ).blockhash

          const signedTx = await anchorProvider.wallet.signTransaction(txn)

          const sigs = await bulkSendRawTransactions(
            anchorProvider?.connection,
            [signedTx.serialize()],
          )

          if (!sigs || sigs.length === 0) {
            throw new Error('Transaction Error')
          }
        } catch (error) {}
      }),
    )

    setCanceling(false)
    // trigger a refresh
    window.location.reload()
  }, [allUncanceledBids, auctionKey])

  const onHide = useCallback(() => {
    setHide(true)
  }, [])

  if (!allUncanceledBids || allUncanceledBids.length === 0 || hide) {
    return null
  }

  return (
    <div className="absolute bottom-4 left-0 right-0">
      <div
        className="px-4 py-3 bg-dark-6 border-dark-4 rounded-lg mx-8 drop-shadow-2xl flex flex-col lg:flex-row items-center gap-4"
        style={{
          borderWidth: '1px',
        }}
      >
        <CloseButton onClick={onHide} />
        <span className="flex-1">{t('auction.detectedUncancelledBids')}</span>
        <button
          onClick={onCancelAll}
          className={`btn font-semibold rounded-md xs:w-full lg:w-auto`}
          style={{
            minWidth: '114.43px',
          }}
        >
          {canceling ? <SimpleSpinner /> : t('auction.cancelAll')}
        </button>
      </div>
    </div>
  )
}

export default CancelBidsBanner
